// src/pages/purchase-requisition/summary-pr/components/SummaryPRTable.jsx
import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, TablePagination, Checkbox, Box, Tooltip, Typography
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { formatDateTime, formatDate, formatTime, getDayName } from "../../history-pengajuan/utils/formatters";
import { UserTooltipDisplay } from "../../PengajuanBarang";

/**
 * Renders the table displaying purchase order summary.
 * @param {object} props - Component props.
 * @param {Array} props.orders - List of approved order data.
 * @param {object} props.userProfile - User's profile data.
 * @param {string} props.invManagementFreshPineOtherRoleId - Role ID for "Inv Management FreshPine & Other".
 * @param {string} props.developerRoleId - Role ID for "Developer".
 * @param {function} props.handleDownloadExcel - Handler to download order data to Excel.
 * @param {Set} props.selectedOrders - Set of selected order IDs.
 * @param {boolean} props.selectAll - Whether all orders are selected.
 * @param {function} props.onSelectOrder - Handler for selecting individual orders.
 * @param {function} props.onSelectAll - Handler for selecting all orders.
 * @param {function} props.getTanggalCreatInfo - Function to get tanggal creat info for tooltip.
 * @param {function} props.formatTanggalCreat - Function to format tanggal creat for display.
 * @param {function} props.getSubmissionInfo - Function to get submission info for tooltip.
 */
const SummaryPRTable = ({
    orders,
    userProfile,
    invManagementFreshPineOtherRoleId,
    developerRoleId,
    handleDownloadExcel,
    selectedOrders,
    selectAll,
    onSelectOrder,
    onSelectAll,
    getTanggalCreatInfo, // Prop baru
    formatTanggalCreat, // Prop baru
    getSubmissionInfo, // Prop baru
}) => {
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Handlers for pagination changes
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page when rows per page changes
    };

    // Helper function to get the final approval date and time (diupdate seperti di HistoryOrderTable)
    const getFinalApprovalInfo = (order) => {
        if (!order.approval_history || !Array.isArray(order.approval_history)) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        // Find the last approval entry
        const finalApproval = order.approval_history
            .filter(entry => entry.decision === "Approved")
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        
        if (!finalApproval || !finalApproval.timestamp) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        const approvalDate = new Date(finalApproval.timestamp);
        
        return {
            date: formatDate(approvalDate, 'DD/MM/YYYY'),
            day: getDayName(approvalDate),
            time: formatTime(approvalDate, 'HH.mm.ss'),
            fullDate: approvalDate
        };
    };

    // Slice the orders array for current page display
    const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Check if user has download permissions (Developer, Inv Management FreshPine & Other)
    const userRoles = userProfile?.id_role ? userProfile.id_role.split(';') : [];
    const canDownloadOrders = userRoles.includes(developerRoleId) || 
                             userRoles.includes(invManagementFreshPineOtherRoleId);

    return (
        <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox
                                indeterminate={selectedOrders.size > 0 && selectedOrders.size < orders.length}
                                checked={selectAll || (orders.length > 0 && selectedOrders.size === orders.length)}
                                onChange={onSelectAll}
                            />
                        </TableCell>
                        <TableCell>No</TableCell>
                        <TableCell>Order Title</TableCell>
                        <TableCell>Purchase Request No</TableCell>
                        <TableCell>Requested By</TableCell>
                        <TableCell>PR Created Date</TableCell>
                        <TableCell>Approved Date</TableCell>
                        <TableCell>Submission Date</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedOrders.length > 0 ? (
                        paginatedOrders.map((order, index) => {
                            const approvalInfo = getFinalApprovalInfo(order);
                            const tanggalCreatInfo = getTanggalCreatInfo(order); // Ambil info tanggal creat
                            const submissionInfo = getSubmissionInfo(order); // Ambil info pengajuan
                            
                            return (
                                <TableRow key={order.id}>

                                    {/* Checkbox */}
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedOrders.has(order.id)}
                                            onChange={() => onSelectOrder(order.id)}
                                        />
                                    </TableCell>

                                    {/* No */}
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                                    {/* Order Title */}
                                    <TableCell>{order.order_title || '-'}</TableCell>

                                    {/* Purchase Request No */}
                                    <TableCell>{order.no_pr || '-'}</TableCell>

                                    {/* Requested By */}
                                    <TableCell>
                                        <UserTooltipDisplay
                                            uid={order.created_by_uid}
                                            userName={order.created_by_name}
                                        />
                                    </TableCell>

                                    {/* PR Created Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {tanggalCreatInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {tanggalCreatInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {formatTanggalCreat(order.tanggal_creat)}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Approved Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {approvalInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {approvalInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {approvalInfo.date}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Submission Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {submissionInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {submissionInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {submissionInfo.date}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                        {canDownloadOrders && (
                                            <IconButton size="small" color="secondary" onClick={() => handleDownloadExcel(order)}>
                                                <FileDownloadIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>

                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={9} align="center">Tidak ada data PR.</TableCell> {/* Diubah dari 8 menjadi 9 */}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={orders.length} // Total number of items
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Baris per halaman:"
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                }
            />
        </TableContainer>
    );
};

export default SummaryPRTable;